import type { DspMetrics } from './index'

/** Engineering contract, not a clinically calibrated scale; never pool versions
 * in analysis. v3 = the v2 control law on artifact-corrected RR (`rrArtifacts.ts`). */
export const REGULATION_CONTROL_VERSION = 'rmssd-primary-v3'
/** What `vrs-sbc/rpi_bridge_ble/dsp_engine.py` computes today: v2, no RR
 * artifact correction. Accepted as-is and labelled as such until the Pi ports v3. */
export const PI_DSP_FORMULA_VERSION = 'rmssd-primary-v2'
/** Beat-to-beat RR level index in two layers (`hrBeat.ts`). Quest only
 * (polar_direct), selected from the operator panel; the Pi does not compute it.
 * v2 (2026-10-27): one anchor per layer; W = 20 s by default until 2026-10-29,
 * 10 s since (same formula, the window travels in `cardio_config`). */
export const HR_BEAT_VERSION = 'hr-beat-v2'
/** Single live R: the v2 control statistic, with no fast feedback branch. */
export const CANONICAL_HR_BEAT_VERSION = 'hr-beat-v3'
/** Previous hr-beat contract (single anchor, W = 5 s by default); still found in bronze. */
export const HR_BEAT_V1_VERSION = 'hr-beat-v1'
/** Every hr-beat formula: historical dual-layer v1/v2 and canonical v3. */
export const HR_BEAT_VERSIONS: readonly string[] = [HR_BEAT_V1_VERSION, HR_BEAT_VERSION, CANONICAL_HR_BEAT_VERSION]

/** True for any hr-beat `formula_version` / `control_version` (v1 bronze included). */
export function isHrBeatVersion(version: string | null | undefined): boolean {
  return version != null && HR_BEAT_VERSIONS.includes(version)
}
export const RMSSD_LOG_UNIT = Math.log(1.25)
const clip = (n: number) => Math.max(-3, Math.min(3, n))
const sigmoid = (n: number) => 1 / (1 + Math.exp(-n / 1.5))
const positive = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0

/** +25% / -20% RMSSD are symmetric ±1 control units. This explicit sensitivity
 * anchor fixes the old z-HR vs unscaled log-RMSSD mismatch; it is not a claim
 * that those changes prove intentional regulation. HR has zero control weight. */
export function rmssdControl(current: number | null, reference: number | null, sclReturnZ: number | null = null) {
  if (!positive(current) || !positive(reference)) return null
  const rmssdZ = clip(Math.log(current / reference) / RMSSD_LOG_UNIT)
  const edaZ = sclReturnZ != null && Number.isFinite(sclReturnZ) ? clip(sclReturnZ) : null
  return {
    rmssdZ,
    rCardio: sigmoid(rmssdZ),
    rCardioEda: edaZ == null ? null : sigmoid(0.6 * rmssdZ + 0.4 * edaZ),
  }
}

/** Compatibility for deployed bridges. v2 (the Pi today) is the same control
 * law without artifact correction: used as-is, keeping its label. hr-beat-v2
 * and hr-beat-v1 (Quest engine, v1 in older data) are also passed through
 * untouched: their `r_live_cardio` is already the slow control layer R_c. v1: transform
 * its measured features, never its HR-led R, into v2 (its RR were not corrected
 * either). Windows/references stay on the Pi, not recomputed here. Unknown
 * formula versions fail closed. Original messages remain in raw storage. */
export function controlDspMetrics(metrics: DspMetrics): DspMetrics {
  if (metrics.formula_version === REGULATION_CONTROL_VERSION || metrics.formula_version === PI_DSP_FORMULA_VERSION
    || isHrBeatVersion(metrics.formula_version)) {
    return metrics
  }
  const c = metrics.cardio?.components ?? {}
  const e = metrics.eda?.components ?? {}
  const eligible = metrics.formula_version === 'live-rolling-v1' && metrics.cardio?.quality === 'good'
  const sclOk = ['good', 'tonic_only'].includes(metrics.eda?.quality)
  const control = eligible ? rmssdControl(c.current_rmssd_ms ?? null, c.baseline_rmssd_ms ?? null,
    sclOk ? e.scl_return_z ?? null : null) : null
  return {
    ...metrics,
    source_formula_version: metrics.formula_version,
    formula_version: PI_DSP_FORMULA_VERSION,
    cardio: { ...metrics.cardio, r_live_cardio: control?.rCardio ?? null,
      quality: control ? 'good' : 'insufficient', reason: control ? null : 'rmssd_control_unavailable',
      components: { ...c, cardio_raw: control?.rmssdZ ?? null, rmssd_control_z: control?.rmssdZ ?? null } },
    eda: { ...metrics.eda, r_live_cardio_eda: control?.rCardioEda ?? null },
  }
}
